import * as React from 'react';
import { useTranslation } from "react-i18next";

import { AutoComplete } from '@progress/kendo-react-dropdowns';
import { FloatingLabel } from '@progress/kendo-react-labels';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";

import { ProcessError, ErrorCode } from '../../classes/ExceptionProcessor';
import { ListOfPaged, MinimumPage } from '../../classes/consts';

import FarmsService from "../../services/farms.service";

export default function FarmLocationsComponents(props) {
    const { t } = useTranslation();
    // Error Hooks
    const [visibleError, setVisibleError] = React.useState(false);
    const [errorData, setErrorData] = React.useState({ messageError: "", code: "" });

    // Grid Hooks
    const [orderByString, setOrderByString] = React.useState("?order=description asc");
    const [sortState, setSortState] = React.useState([]);
    const [pageState, setPageState] = React.useState({ skip: 0, take: MinimumPage() });

    // Loading Hooks
    const [loading, setLoading] = React.useState(true);
    const closeError = React.useCallback(() => { setVisibleError(false) }, []);

    const [data, setData] = React.useState([]);

    //#region DataFilters
    //#region Location Description Filter
    const [locationDescriptionFilter, setlocationDescriptionFilter] = React.useState("");

    const handleLocationDescriptionFilterChange = (event) => {
        const value = event.value || "";
        setlocationDescriptionFilter(value);
    };
    //#endregion
    
    //#region Sorting Filter
    const applySortingAndFiltering = React.useCallback((sortOverride) => {
        const sort = sortOverride || sortState;

        const sortField = sort?.[0]?.field || "description";
        const sortDir = sort?.[0]?.dir || "asc";
        const order = `${sortField} ${sortDir}`;

        const filterParts = [];

        if (locationDescriptionFilter) filterParts.push(`contains(description,'${locationDescriptionFilter}')`);

        const filter = filterParts.join(" and ");
        const queryString = `?order=${encodeURIComponent(order)}${filter ? `&filter=${encodeURIComponent(filter)}` : ""}`;

        setOrderByString(queryString);
    }, [sortState, locationDescriptionFilter]);
    //#endregion
    //#endregion

    React.useEffect(() => 
    {
        if (props.farm.farmId !== 0)
        {
            setLoading(true);

            FarmsService.getFilteredFarmLocations(props.farm.farmId, orderByString)
                .then(response => response.data)
                .then(json => {
                    setData(json.contract || []);
                    setLoading(false);
                }).catch(err => {
                    setErrorData({ messageError: ProcessError(err), code: ErrorCode(err) });
                    setVisibleError(true);
                    setLoading(false);
                });
        }
    }, [props.farm, orderByString]);

    const loadingPanel =
        <div className="k-loading-mask">
            <span className="k-loading-text">Loading</span>
            <div className="k-loading-image"></div>
            <div className="k-loading-color"></div>
        </div>;

    const loadingCell = React.useCallback((tdElement, cellProps) => {
        if (cellProps.dataItem[cellProps.field] === undefined) {
            // shows loading cell if no data
            return <td> <span className="k-placeholder-line"></span></td>;
        }

        // default rendering for this cell
        return tdElement;
    }, []);

    const sortChange = React.useCallback((event) => {
        setSortState(event.sort);
        applySortingAndFiltering(event.sort);
    });

    const pageChange = React.useCallback((event) => {
        setPageState({ ...pageState, skip: event.page.skip, take: event.page.take });
    }, [pageState]);

    const processedData = (data.length > 0 || locationDescriptionFilter
        ? data
        : data).slice(pageState.skip, pageState.skip + pageState.take);

    return (
        <div>
            {visibleError && (
                <Dialog title={"Error " + errorData.code} onClose={closeError}>
                    <p style={{ margin: "25px", textAlign: "center" }}>
                        <b>{errorData.messageError}</b>
                    </p>
                    <DialogActionsBar>
                        <button className="k-button" onClick={() => { setVisibleError(false) }}>
                            OK
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {loading && loadingPanel}

            <div className="container-fluid">
                <div className="card">
                    <div className="card-header m-0 p-3">
                        <div className="row g-3 w-100 align-items-end">

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("FarmGroups_GroupDescription")} editorId="descriptionInput">
                                    <AutoComplete
                                        id="descriptionInput"
                                        loading={loading}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(farmLocation => farmLocation.description))]}
                                        value={locationDescriptionFilter}
                                        onChange={handleLocationDescriptionFilterChange}
                                    />
                                </FloatingLabel>
                            </div>


                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <button
                                    className="k-button k-primary w-100"
                                    style={{ marginTop: '25px' }}
                                    onClick={() => applySortingAndFiltering()}
                                    title={t("Farms_Button_ApplySorting")}
                                >
                                    {t("Farms_Button_ApplySorting")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <br />

                <div className="table-responsive">
                    <Grid
                        cellRender={loadingCell}
                        pageable={{ pageSizes: ListOfPaged() }}
                        data={processedData}
                        skip={pageState.skip}
                        take={pageState.take}
                        total={
                            (data.length > 0 ||
                                locationDescriptionFilter
                                ? data.length
                                : data.length)
                        }
                        scrollable={'none'}
                        sortable={{ allowUnsort: true, mode: "single" }}
                        sort={sortState}
                        onSortChange={sortChange}
                        onPageChange={pageChange}
                    >
                        <Column field="locationId" title={t("Farms_Status_Identificator")} sortable={true} />
                        <Column field="description" title={t("FarmLocations_Description")} sortable={true} />
                        <Column field="heads" title={t("FarmLocations_Capacity")} sortable={true} />
                    </Grid>
                </div>
            </div>
        </div>
    );
}